/* ----------------------------------------------------------------------------
 * GAP #1 — direct keyword-targeted content generation: PURE contract + model.
 *
 * SINGLE contract-reconcile module (mirrors connections-model.ts). Imports
 * NOTHING (no lib/api, no import.meta) so it is unit-testable under
 * `node --test --experimental-strip-types`. The thin api glue that POSTs lives
 * in content-generate.ts.
 *
 * RECONCILED to the ACTUAL BE contract (branch content/gap1-backend @55ec081,
 * docs/verify/gap1be/build-note-2026-07-31.md) — 2026-07-31.
 *
 * TWO INPUT MODES feed ONE server-side generate path
 * (KeywordTargetedContentService::generate -> ContentEngine::generateDraft,
 * which runs checkDuplicate() UNCONDITIONALLY before any spend — a duplicate is
 * refused 409; neither mode can ship as a dedup bypass):
 *   MODE (i)  DROPDOWN-SELECT a tracked keyword (Keywords row-action OR the
 *             Copilot picker). Zero free-text parse — the FE sends ONLY the
 *             keyword_id; the BE takes the project from the keyword row and
 *             resolves target/evidence via SeoOpportunitySelector::targetForTopic.
 *   MODE (ii) STRUCTURED PROMPT. The FE forwards the RAW prompt verbatim (+ the
 *             active project_id); the BE does all extraction + target-page
 *             validation against the gap#2 site inventory.
 *
 * ============================================================================
 *  ACTUAL FE<->BE CONTRACT (the reconcile point — everything the lead must
 *  touch to realign lives in THIS file).
 * ============================================================================
 *  MODE (i)  POST /v1/content/drafts/from-keyword   body: { keyword_id: int }
 *  MODE (ii) POST /v1/content/drafts/from-prompt    body: { prompt, project_id }
 *  SUCCESS 201: { ok:true, draft:{...}, meta:{mode, project_id, keyword, topic,
 *               target_page|null, location|null} }
 *  409 DUPLICATE: { error:{code:'duplicate_content', message},
 *               duplicate:{ reason, matched:{source:'wp_post'|'draft', slug,
 *               title, url}, site } }  (site is '' on this direct path — a site
 *               label is derived from matched.url when present).
 *  422: draft_generation_failed{reason} | invalid_project |
 *       unknown_target_page{target_page_url} | target_index_unavailable{target_page_url}
 *  400: invalid_request | invalid_prompt   404: not_found (keyword absent/foreign)
 *  413: too large   401/403: auth/CSRF/no-active-workspace gate
 * -------------------------------------------------------------------------- */

/** The two generate endpoints — the reconcile point if the BE paths differ. */
export const CONTENT_GENERATE_ENDPOINTS = {
  /** MODE (i): generate from a selected tracked keyword (send keyword_id only). */
  fromKeyword: '/content/drafts/from-keyword',
  /** MODE (ii): generate from a raw structured prompt (BE extracts). */
  fromPrompt: '/content/drafts/from-prompt',
} as const;

/** BE cap on the structured prompt (413 above this). */
export const PROMPT_MAX_LEN = 500;

/** The EXISTING connector manual-sync route (session+CSRF+workspace gated) used
 *  by the fail-closed "inventory not synced -> Sync now" moment. Building it
 *  here keeps the endpoint a contract constant beside the generate paths. */
export const INDEX_SYNC_ENDPOINT = (targetId: number): string =>
  '/connector/targets/' + targetId + '/index-sync';

/** The connected site's synced published-content index (pages/posts/categories/
 *  cpts) for the stacked "target page" picker. Optional ?topic= yields a
 *  suggested_target for pre-selection. Read-only; session+workspace gated. */
export const CONNECTOR_TARGET_INDEX_ENDPOINT = (targetId: number): string =>
  '/connector/targets/' + targetId + '/index';

/** A tracked keyword the user can select for MODE (i) (subset of SeoKeywordRow).
 *  `id` is the bc_seo_keywords row id (string in the FE row model) == keyword_id. */
export interface SelectableKeyword {
  id: string;
  keyword: string;
  location_code?: number | null;
  language?: string | null;
}

/** MODE (i) request body — the keyword_id, plus an OPTIONAL owner-chosen
 *  internal-link target (option b). When omitted the body is byte-identical to
 *  the original {keyword_id} contract. */
export interface KeywordGenerateRequest {
  keyword_id: number;
  target_page_url?: string;
}

/** MODE (ii) request body — the RAW prompt (BE extracts) + the active project. */
export interface PromptGenerateRequest {
  prompt: string;
  project_id: number;
}

/** The API projection of the generated draft (subset we render). */
export interface GeneratedDraftDto {
  id: number;
  content_type?: string;
  status?: string;
  title?: string;
  slug?: string;
}

/** meta echoed on the 201 (what the BE resolved) — optional passthrough. */
export interface GenerateMeta {
  mode?: string;
  project_id?: number;
  keyword?: string;
  topic?: string;
  target_page?: string | null;
  location?: string | null;
  /* Title-decoupling / staleness contract (additive on the 201 meta envelope). */
  angle?: string;
  angle_source?: string;
  /** ISO-8601 Z stamp of the published-inventory baseline the angle varied
   *  against; present when the BE proceeded on a STALE-but-synced index. */
  inventory_as_of?: string | null;
  inventory_stale?: boolean;
}

/** The 201 success envelope both modes return. */
export interface GenerateSuccessResponse {
  ok?: boolean;
  draft: GeneratedDraftDto;
  meta?: GenerateMeta;
}

/** The 409 top-level duplicate block (sibling of `error`). */
export interface DuplicateMatched {
  source?: string; // 'wp_post' = published on site · 'draft' = existing BC draft
  slug?: string;
  title?: string;
  url?: string;
}
export interface DuplicateBlock {
  reason?: string;
  matched?: DuplicateMatched;
  site?: string;
}

/** What the surfaces render — a ready draft, a graceful duplicate, or an honest
 *  validation/failure message. */
export type GenerateOutcome =
  | {
      kind: 'ready';
      draftId: number;
      title: string;
      slug: string;
      targetPage: string | null;
      location: string | null;
      /** STALE-but-synced note source: when the BE proceeded on an inventory
       *  older than its freshness threshold it stamps the baseline date, which
       *  the surfaces render as a non-blocking "as of {date}" note. */
      inventoryAsOf: string | null;
      inventoryStale: boolean;
    }
  | { kind: 'duplicate'; message: string; slug: string | null; site: string | null }
  | { kind: 'invalid'; message: string }
  | { kind: 'inventory_not_synced'; message: string; syncTargetId: number | null }
  /** A 422 draft_generation_failed — the generate ran but produced nothing
   *  usable. `retryable:false` = a reuse/cannibalisation reason where the SAME
   *  keyword will keep failing (pick a different keyword); `retryable:true` = a
   *  transient/quality reason (or unknown) where a retry may succeed. Rendered
   *  in a CLEARLY-VISIBLE tone so a failure is never mistaken for "nothing
   *  happened". */
  | { kind: 'failed'; message: string; retryable: boolean };

/* --- MODE (i): build the keyword-generate request from a selected row ------ */

/** Coerce the FE string row id to a numeric keyword_id. */
function numericKeywordId(id: string): number | undefined {
  const n = Number(id);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

/**
 * Build the MODE (i) request from a SELECTED tracked keyword. Sends ONLY
 * keyword_id — the BE takes the project from the keyword row and resolves
 * target/evidence server-side. Fails closed if the row id is not a positive int
 * (the surfaces only offer real rows, but the builder never POSTs junk).
 */
export function buildKeywordGenerateRequest(
  keyword: SelectableKeyword,
  targetPageUrl?: string | null,
): KeywordGenerateRequest {
  const kid = numericKeywordId(keyword?.id ?? '');
  if (kid === undefined) throw new Error('invalid_keyword_id');
  const req: KeywordGenerateRequest = { keyword_id: kid };
  // Only attach a target when the user actually picked one -> the empty-selection
  // body stays byte-identical to the original { keyword_id } contract.
  const t = (targetPageUrl ?? '').trim();
  if (t !== '') req.target_page_url = t;
  return req;
}

/* --- MODE (ii): forward the raw prompt (BE extracts) ----------------------- */

/**
 * Build the MODE (ii) request. The FE forwards the RAW prompt verbatim — it
 * does NOT parse topic/target_page/location (the BE does). Trims + guards
 * emptiness/length so a blank or over-cap prompt fails closed before a call.
 */
export function buildPromptGenerateRequest(
  prompt: string,
  projectId: number | null,
): PromptGenerateRequest {
  if (projectId == null) throw new Error('no_active_project');
  const p = (prompt ?? '').trim();
  if (p === '') throw new Error('empty_prompt');
  if (p.length > PROMPT_MAX_LEN) throw new Error('prompt_too_long');
  return { prompt: p, project_id: projectId };
}

/* --- success interpretation (201) ----------------------------------------- */

export function interpretGenerateSuccess(res: GenerateSuccessResponse): GenerateOutcome {
  const d = res?.draft;
  const m = res?.meta;
  return {
    kind: 'ready',
    draftId: Number(d?.id) || 0,
    title: typeof d?.title === 'string' ? d.title : '',
    slug: typeof d?.slug === 'string' ? d.slug : '',
    targetPage: m && typeof m.target_page === 'string' && m.target_page !== '' ? m.target_page : null,
    location: m && typeof m.location === 'string' && m.location !== '' ? m.location : null,
    inventoryAsOf:
      m && typeof m.inventory_as_of === 'string' && m.inventory_as_of !== '' ? m.inventory_as_of : null,
    inventoryStale: !!(m && m.inventory_stale === true),
  };
}

/* --- 409 DUPLICATE UX (both modes) ---------------------------------------- */

/** Host from a URL, for a site label when the 409's `site` is '' (direct path). */
function hostOf(url: string | undefined): string | null {
  if (!url || typeof url !== 'string') return null;
  const m = url.match(/^https?:\/\/([^/]+)/i);
  return m ? m[1] : null;
}

/**
 * Render the 409 duplicate block as a CLEAR "already exists / already published
 * on <site>" message with the matched slug/site — NOT a generic error. Serves
 * BOTH modes and BOTH flavours: a pre-existing PUBLISHED site page
 * (matched.source==='wp_post', gap#2 inventory awareness) or an existing BC
 * draft (matched.source==='draft'). This is the FE 409-mapping the dedup lane
 * deferred.
 */
export function describeDuplicate(dup: DuplicateBlock | undefined): {
  message: string;
  slug: string | null;
  site: string | null;
} {
  const matched = dup?.matched ?? {};
  const slug = typeof matched.slug === 'string' && matched.slug !== '' ? matched.slug : null;
  const title = typeof matched.title === 'string' && matched.title !== '' ? matched.title : null;
  const siteRaw = typeof dup?.site === 'string' && dup.site !== '' ? dup.site : null;
  const site = siteRaw ?? hostOf(matched.url);
  const where = slug ? `/${slug.replace(/^\/+/, '')}` : title ? `“${title}”` : null;
  const published = matched.source === 'wp_post';

  let message: string;
  if (published) {
    message = site
      ? `That content is already published on ${site}${where ? ` (${where})` : ''}. Nothing was generated — pick a different keyword or angle.`
      : `That content is already published on your site${where ? ` (${where})` : ''}. Nothing was generated — pick a different keyword or angle.`;
  } else {
    message = `A similar draft already exists${where ? ` (${where})` : ''}. Nothing was generated — open the existing one or choose another angle.`;
  }
  return { message, slug, site };
}

/* --- error mapping --------------------------------------------------------- */

/** draft_generation_failed reasons that are NON-retryable: the SAME keyword was
 *  already generated recently / would cannibalise an existing target, so a retry
 *  fails identically — the user must pick a DIFFERENT keyword. Every OTHER reason
 *  (keyphrase_not_in_intro, insufficient_signal, decode_failed, empty_body,
 *  provider_error, provider_not_ready, unresolved_placeholder), and an
 *  unknown/absent reason, is treated as transient/retryable ("try again"). */
export const REUSE_GENERATION_REASONS: ReadonlySet<string> = new Set<string>([
  'focus_keyphrase_reused',
  'topic_cannibalisation',
]);

/** Read the BE `reason` for a 422 draft_generation_failed. The house error
 *  helper (ContentGenerateRoutes::error) places it as a SIBLING of code/message
 *  INSIDE the `error` object — i.e. { error: { code, message, reason } } — NOT
 *  under details/data. Fall back to the flat shapes defensively. */
function generationReasonOf(payload: Record<string, unknown> | undefined): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const err = payload.error as Record<string, unknown> | undefined;
  const data = payload.data as Record<string, unknown> | undefined;
  const v =
    (err?.reason as string | undefined) ??
    (data?.reason as string | undefined) ??
    (payload.reason as string | undefined);
  return typeof v === 'string' && v !== '' ? v : null;
}

/** Dig target_page_url out of a house error payload (nested or flat), for the
 *  Mode (ii) validation messages. */
function targetPageUrlOf(payload: Record<string, unknown> | undefined): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const err = payload.error as Record<string, unknown> | undefined;
  const errDetails = err && (err.details as Record<string, unknown> | undefined);
  const flatDetails = payload.details as Record<string, unknown> | undefined;
  const data = payload.data as Record<string, unknown> | undefined;
  const v =
    (errDetails?.target_page_url as string | undefined) ??
    (flatDetails?.target_page_url as string | undefined) ??
    (data?.target_page_url as string | undefined) ??
    (payload.target_page_url as string | undefined);
  return typeof v === 'string' && v !== '' ? v : null;
}

/** Lift the connector target id out of a 422 target_inventory_not_synced body,
 *  wherever the house envelope placed it (error.sync_target_id [the shape self::error emits], top-level, error.details, data, or
 *  details). Returns null when absent (site not connected) — the surfaces then
 *  render the "connect a site" text with no Sync button. */
function syncTargetIdOf(payload: Record<string, unknown> | undefined): number | null {
  if (!payload || typeof payload !== 'object') return null;
  const err = payload.error as Record<string, unknown> | undefined;
  const errDetails = err && (err.details as Record<string, unknown> | undefined);
  const flatDetails = payload.details as Record<string, unknown> | undefined;
  const data = payload.data as Record<string, unknown> | undefined;
  const raw =
    (err?.sync_target_id as unknown) ??
    (errDetails?.sync_target_id as unknown) ??
    (flatDetails?.sync_target_id as unknown) ??
    (data?.sync_target_id as unknown) ??
    (payload.sync_target_id as unknown);
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * Map an API failure (shape-compatible with lib/api ApiError: { status, code,
 * message, payload }) to a GenerateOutcome, or return null to signal "rethrow /
 * generic error path" (unmapped 4xx/5xx and the 401/403 gate — the client
 * already redirects on a 401 data call). Pure, so it is unit-testable without
 * importing lib/api.
 */
export function mapGenerateError(err: {
  status: number;
  code: string;
  message?: string;
  payload?: unknown;
}): GenerateOutcome | null {
  const payload = (err.payload ?? undefined) as Record<string, unknown> | undefined;

  // 409 — blocking duplicate (top-level `duplicate` block).
  if (err.status === 409 && err.code === 'duplicate_content') {
    const dup = payload?.duplicate as DuplicateBlock | undefined;
    const d = describeDuplicate(dup);
    return { kind: 'duplicate', message: d.message, slug: d.slug, site: d.site };
  }

  // 422 — honest validation / generation failures.
  if (err.status === 422) {
    // Fail-closed staleness contract (title-decoupling spec 2026-08-04): the
    // published-content index was never synced, so the supporting angle has no
    // baseline to vary against. NOT a raw error — an actionable "Sync now"
    // state carrying the connector target to sync.
    if (err.code === 'target_inventory_not_synced') {
      return {
        kind: 'inventory_not_synced',
        message:
          err.message ||
          "This site's published-content index hasn't been synced yet. Sync it first so the new article varies against what's already live.",
        syncTargetId: syncTargetIdOf(payload),
      };
    }
    if (err.code === 'unknown_target_page') {
      const url = targetPageUrlOf(payload);
      return {
        kind: 'invalid',
        message:
          `That target page${url ? ` (${url})` : ''} isn't recognized on your site. ` +
          "Check the URL, or leave it out and I'll pick the best page.",
      };
    }
    if (err.code === 'target_index_unavailable') {
      return {
        kind: 'invalid',
        message:
          "Your site's content inventory isn't available yet, so that target page couldn't be checked. Try again in a moment.",
      };
    }
    if (err.code === 'invalid_project') {
      return { kind: 'invalid', message: 'That domain isn’t valid for generation. Pick a connected domain and try again.' };
    }
    if (err.code === 'draft_generation_failed') {
      const reason = generationReasonOf(payload);
      if (reason !== null && REUSE_GENERATION_REASONS.has(reason)) {
        // Non-retryable: retrying the SAME keyword fails identically.
        return {
          kind: 'failed',
          retryable: false,
          message: 'This keyword was already generated recently — try a different keyword.',
        };
      }
      // Transient / quality reasons (or unknown/missing reason): a retry may work.
      return {
        kind: 'failed',
        retryable: true,
        message: 'The draft couldn’t be generated this time — please try again.',
      };
    }
    return { kind: 'invalid', message: err.message || 'That request could not be validated. Nothing was generated.' };
  }

  // 404 — keyword absent OR foreign (indistinguishable by design).
  if (err.status === 404 && err.code === 'not_found') {
    return { kind: 'invalid', message: 'That keyword couldn’t be found. Refresh the list and try again.' };
  }

  // 400 — malformed request / unusable prompt.
  if (err.status === 400) {
    if (err.code === 'invalid_prompt') {
      return {
        kind: 'invalid',
        message: 'I couldn’t find a clear subject in that prompt. Try naming the topic (and an optional target page + location).',
      };
    }
    if (err.code === 'invalid_request') {
      return { kind: 'invalid', message: 'That request wasn’t valid. Check the details and try again.' };
    }
    return { kind: 'invalid', message: err.message || 'That request wasn’t valid.' };
  }

  // 413 — prompt/body too large.
  if (err.status === 413) {
    return { kind: 'invalid', message: 'That prompt is too long — shorten it and try again.' };
  }

  // Everything else (401/403 gate, 5xx, unknown) -> rethrow / generic.
  return null;
}

/* --- connector target-index (the stacked "target page" picker) ------------- */

/** One indexed item (ConnectorIndexReadRoutes::shape). */
export interface TargetIndexItem {
  id: number;
  kind: string;
  remote_id?: string;
  title?: string;
  url?: string;
  synced_at?: string | null;
}

/** The suggested_target block (present ONLY when ?topic= is sent). `matched` is
 *  the index item whose url equals the resolved SEO landing page, or null. */
export interface TargetIndexSuggestion {
  topic?: string;
  primary_keyword?: string;
  secondary_keywords?: string[];
  target_page?: string;
  position?: number | null;
  matched?: TargetIndexItem | null;
}

/** GET /v1/connector/targets/{id}/index response. NOTE: `cpts` encodes as `[]`
 *  (not `{}`) when empty because PHP json_encode maps an empty assoc array to a
 *  JS array — callers MUST guard both shapes. */
export interface TargetIndexResponse {
  ok?: boolean;
  target_id?: number;
  project_id?: number | null;
  counts?: { posts?: number; categories?: number; pages?: number; cpts?: number };
  posts?: TargetIndexItem[];
  categories?: TargetIndexItem[];
  pages?: TargetIndexItem[];
  cpts?: Record<string, TargetIndexItem[]> | TargetIndexItem[];
  suggested_target?: TargetIndexSuggestion | null;
}

/** A single flat option for the picker <select> (title falls back to url). */
export interface TargetPageOption {
  /** the option group label ('Pages' | 'Posts' | a cpt type name). */
  group: string;
  /** the option VALUE (the page url) driving the internal-link target. */
  url: string;
  /** the visible label (title, falling back to the url). */
  label: string;
}

/** Guard the cpts `{}`-vs-`[]` shape -> always an array of [type, items] groups. */
export function cptGroups(
  cpts: TargetIndexResponse['cpts'],
): Array<[string, TargetIndexItem[]]> {
  if (!cpts || Array.isArray(cpts)) return [];
  return Object.entries(cpts).filter(([, items]) => Array.isArray(items) && items.length > 0);
}

/** Flatten the index response into grouped picker options (pages, then posts,
 *  then each cpt type). Items without a url are dropped (a url is the value). */
export function buildTargetPageOptions(res: TargetIndexResponse | undefined): TargetPageOption[] {
  if (!res) return [];
  const opts: TargetPageOption[] = [];
  const push = (group: string, items: TargetIndexItem[] | undefined) => {
    for (const it of items ?? []) {
      const url = typeof it?.url === 'string' ? it.url.trim() : '';
      if (url === '') continue;
      const title = typeof it?.title === 'string' ? it.title.trim() : '';
      opts.push({ group, url, label: title !== '' ? title : url });
    }
  };
  push('Pages', res.pages);
  push('Posts', res.posts);
  for (const [type, items] of cptGroups(res.cpts)) push(type, items);
  return opts;
}

/** The url to PRE-SELECT for a topic (suggested_target.matched.url), or '' when
 *  there is no confident match — the default 'best-matched page' option. */
export function suggestedTargetUrl(res: TargetIndexResponse | undefined): string {
  const u = res?.suggested_target?.matched?.url;
  return typeof u === 'string' && u.trim() !== '' ? u.trim() : '';
}
